import React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { ActionBar } from './styled';
import { CopyOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import copy from 'copy-to-clipboard';
import { chartIntervalSelector } from '@store/trends/birdChart/selectors';
import AskNexaViewerButton from '@src/components/nexa/AskNexaViewerButton';
import { toStartMs } from '@src/components/nexa/utils/viewerDeepLink';

const actions = [{ label: <CopyOutlined />, value: 'copy' }];

interface ActionsProps {
  /** SessionId — the target of the row-level actions (copy button + Nexa). */
  data: string;
  /** ClientId + start time for the same session. When present + valid, Nexa
   *  opens the session diagnostic. Absent => viewer landing overview. */
  clientId?: string | number | null;
  sessionStartTime?: string | number | null;
  sessionEndTime?: string | number | null;
  viewerId?: string | number | null;
}

function Actions({ data, clientId, sessionStartTime, sessionEndTime, viewerId }: ActionsProps) {
  const interval = useSelector(chartIntervalSelector) as any;

  const handleActions = (value) => {
    if (value === 'copy') {
      return (event) => {
        // skip trigger cell actions like showing session details
        event.stopPropagation();
        if (copy(data, { format: 'text/plain' })) {
          message.info('Copied to clipboard');
        }
      };
    }
    return () => null;
  };

  // Derive the raw millisecond timestamp so the deep link can hand nexa-client
  // the exact SessionStartTimestampMs. `toStartMs` guards against upstream
  // shapes we've seen in the wild (empty object, negative epoch, unparseable
  // string) — moment({}) silently returns "now", so a naive valueOf() would
  // build a valid-looking-but-wrong current-time diagnostic link.
  const startMs = toStartMs(sessionStartTime);
  const session =
    clientId && data && startMs !== null
      ? {
          sessionId: data,
          clientId,
          sessionStartMs: startMs,
          sessionEndMs: toStartMs(sessionEndTime),
          sessionStart: moment(startMs).toISOString(),
        }
      : null;

  return (
    <ActionBar className="action-bar">
      {actions.map((action) => (
        <Button
          key={action.value}
          type="text"
          icon={action.label}
          style={{ color: '#0046ff' }}
          onClick={handleActions(action.value)}
        />
      ))}
      <AskNexaViewerButton
        viewerId={viewerId}
        from={interval?.from}
        to={interval?.to}
        session={session}
        compact
      />
    </ActionBar>
  );
}

export default Actions;
